import { Api } from '@/api/api.client';
import { Components } from '@/api/generated/api';
import { WHO_AM_I_QUERY_KEY } from '@/api/query.keys';
import { fetchWhoAmI, switchUserRole } from '@/api/resources/auth.api';
import { useAuth } from '@/auth/useAuth';
import { useAvatarPictures } from '@/composables/useAvatarPictures';
import { syncLocaleFromWhoAmI } from '@/composables/useLocale';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useToast } from '@/composables/useToast';
import { useCurrentClassStore } from '@/stores/useCurrentClassStore';
import { useLastViewedStore } from '@/stores/useLastViewedStore';
import { useWhoAmIStore } from '@/stores/useWhoAmIStore';
import { getCurrentRole, getCurrentTenant, getIsCurrentTenantSingleUser } from '@/utils/role.utils';
import { useQueryClient } from '@tanstack/vue-query';
import { NameRepresentation } from 'common/enum/user.setting.enum';
import { getUserFullName } from 'common/utils/name.utils';
import { computed, ComputedRef, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

interface IWhoAmIComposable {
  changeEmail: (dto: Components.Schemas.RequestEmailChangeDto) => Promise<void>;
  changePassword: (dto: Components.Schemas.ChangePasswordDto) => Promise<void>;
  changeUserRole: (userRoleId: number, redirect?: boolean) => Promise<void>;
  confirmEmailChange: (dto: Components.Schemas.ConfirmEmailChangeDto) => Promise<void>;
  currentTenant: Ref<Components.Schemas.TenantDto | undefined>;
  currentUserRole: ComputedRef<Components.Schemas.UserRoleWithTenantMetaDataDto | undefined>;
  /** First name before last name, independent of the user's name representation setting. */
  fullName: ComputedRef<string>;
  getWhoAmIWithAuthHandling: () => Promise<Components.Schemas.WhoAmIDataDto | null>;
  isWhoAmIDataError: Ref<Error | null>;
  isWhoAmIDataPending: Ref<boolean>;
  isCurrentTenantSingleUser: ComputedRef<boolean>;
  uploadProfileAvatarPicture: (file: File, showToast?: boolean) => Promise<void>;
  updateSource: (dto: Components.Schemas.UpdateUserSourceDto, showToast?: boolean) => Promise<void>;
  updateProfile: (dto: Components.Schemas.UpdateUserDto, showToast?: boolean) => Promise<void>;
  whoAmI: ComputedRef<Components.Schemas.WhoAmIDataDto | null>;
  refresh: () => Promise<void>;
}

export function useWhoAmI(): IWhoAmIComposable {
  const whoAmIStore = useWhoAmIStore();
  const currentClassStore = useCurrentClassStore();
  const lastViewedStore = useLastViewedStore();
  const { editSuccessToast, catchError, customSuccessToast, showError } = useToast();
  const { isLoggedIn, logout } = useAuth();
  const { uploadUserAvatarPicture } = useAvatarPictures();
  const { push } = useRouter();
  const { t } = useI18n();

  const queryClient = useQueryClient();

  async function loadWhoAmI(): Promise<Components.Schemas.WhoAmIDataDto> {
    const whoAmI = await fetchWhoAmI();
    whoAmIStore.setWhoAmI(whoAmI);
    syncLocaleFromWhoAmI(whoAmI);
    return { ...whoAmI };
  }

  const whoAmIQueryKey = computed(() => [...WHO_AM_I_QUERY_KEY, isLoggedIn.value]);
  const {
    data,
    isPending: isWhoAmIDataPending,
    error: isWhoAmIDataError,
  } = useQueryWithToast({
    queryKey: whoAmIQueryKey,
    enabled: isLoggedIn.value,
    queryFn: loadWhoAmI,
    meta: {
      showErrorToast: false,
    },
  });

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: whoAmIQueryKey });
  }

  async function getWhoAmIWithAuthHandling(): Promise<Components.Schemas.WhoAmIDataDto | null> {
    if (!isLoggedIn.value) return null;

    try {
      return await loadWhoAmI();
    } catch (e: any) {
      const status = e?.status ?? e?.response?.status;
      showError(e);

      if (status === 401) {
        await logout();
      }

      return null;
    }
  }

  async function uploadProfileAvatarPicture(file: File, showToast = true): Promise<void> {
    await catchError(async () => {
      await uploadUserAvatarPicture(file);
      if (showToast) editSuccessToast();
    });
  }

  async function updateProfile(dto: Components.Schemas.UpdateUserDto, showToast = true): Promise<void> {
    await catchError(async () => {
      await Api.Routes.User.update(null, dto);
      await refresh();
      if (showToast) editSuccessToast();
    });
  }

  async function updateSource(dto: Components.Schemas.UpdateUserSourceDto, showToast = false): Promise<void> {
    await catchError(async () => {
      await Api.Routes.User.updateSource(null, dto);
      await refresh();
      if (showToast) editSuccessToast();
    });
  }

  async function changeUserRole(userRoleId: number, redirect = true): Promise<void> {
    await catchError(async () => {
      await switchUserRole(userRoleId);
      currentClassStore.$reset();
      lastViewedStore.$reset();
      await getWhoAmIWithAuthHandling();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['currentTenant'] }),
        queryClient.invalidateQueries({ queryKey: ['currentTenantOwnersAndAdmins'] }),
      ]);

      if (redirect) {
        await push('/');
        location.reload();
      }
    });
  }

  async function changeEmail(dto: Components.Schemas.RequestEmailChangeDto): Promise<void> {
    await catchError(async () => {
      await Api.Routes.EmailChange.requestEmailChange(null, dto);
      customSuccessToast(t('myAccount.form.changeEmailSuccessInquiry'));
    });
  }

  async function changePassword(dto: Components.Schemas.ChangePasswordDto): Promise<void> {
    await catchError(async () => {
      await Api.Routes.PasswordReset.changePassword(null, dto);
      customSuccessToast(t('myAccount.form.changePasswordSuccess'));
    });
  }

  async function confirmEmailChange(dto: Components.Schemas.ConfirmEmailChangeDto): Promise<void> {
    await catchError(async () => {
      await Api.Routes.EmailChange.confirmEmailChange(null, dto);
      customSuccessToast(t('myAccount.form.changeEmailSuccess'));
    });
  }

  const whoAmI = computed(() => data.value ?? null);
  const currentTenant = computed(() => getCurrentTenant(data.value));
  const currentUserRole = computed(() => getCurrentRole(data.value));
  const fullName = computed(() => getUserFullName(data.value ?? {}, NameRepresentation.firstNameBeforeLastName));
  const isCurrentTenantSingleUser = computed(() => getIsCurrentTenantSingleUser(data.value));

  return {
    changeEmail,
    changePassword,
    changeUserRole,
    confirmEmailChange,
    currentTenant,
    currentUserRole,
    fullName,
    getWhoAmIWithAuthHandling,
    isWhoAmIDataError,
    isWhoAmIDataPending,
    isCurrentTenantSingleUser,
    uploadProfileAvatarPicture,
    updateSource,
    updateProfile,
    whoAmI,
    refresh,
  };
}
